import { directionalProperty, rem } from 'polished';
import styled, { css } from 'styled-components';

import { flexStyles, StyledFlexProps } from '../../styles';
import { FontSizeTheme, IconSize } from '../../theme';
import { subtractUnit } from '../../utils';
import InputButtonPrimitive from '../Input/primitives/InputButtonPrimitive';

import getSwitchThemeVariant from './getSwitchThemeVariant';
import { SwitchElementSize } from './types';

const PILL_BORDER_WIDTH = '1px';

const fontSizeKeyByElementSize: Record<SwitchElementSize, keyof FontSizeTheme> =
  {
    big: 'md',
    small: 'sm',
  };

export const SwitchRoot = styled.label<StyledFlexProps>`
  display: flex;
  align-items: center;
  ${flexStyles}
`;

// Pills

type CommonPillProps = { $elementSize: SwitchElementSize };

const commonPillStyles = css<CommonPillProps>(({ theme, $elementSize }) => {
  const variant = getSwitchThemeVariant(theme, $elementSize);

  const pillText = variant['pill-text'];

  const verticalPadding = subtractUnit(
    pillText['vertical-padding'],
    PILL_BORDER_WIDTH,
  );

  const horizontalPadding = subtractUnit(
    pillText['horizontal-padding'],
    PILL_BORDER_WIDTH,
  );

  return css`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;

    border: ${PILL_BORDER_WIDTH} solid transparent;
    border-radius: ${pillText.radius};

    white-space: nowrap;
    font-size: ${theme.core.typography.fontSize[
      fontSizeKeyByElementSize[$elementSize]
    ]};
    line-height: ${rem(16)};

    ${directionalProperty('padding', verticalPadding, horizontalPadding)}
  `;
});

export const InactivePill = styled.span<CommonPillProps>`
  ${commonPillStyles}
  flex: 1 1 auto;
`;

type ActivePillProps = CommonPillProps & {
  $isColored?: boolean;
  $isChecked?: boolean;
  $isDisabled?: boolean;
};

export const ActivePill = styled.mark<ActivePillProps>(
  ({ theme, $elementSize, $isChecked, $isColored, $isDisabled }) => {
    const variant = getSwitchThemeVariant(theme, $elementSize);
    const pillText = variant['pill-text'];

    const getPillColors = () => {
      if ($isDisabled) {
        return {
          fg: pillText['fg-disabled'],
          bg: pillText['bg-disabled'],
          border: pillText['border-active'],
        };
      }

      if (!$isColored) {
        return {
          fg: pillText['fg-default'],
          bg: pillText['bg-active'],
          border: pillText['border-active'],
        };
      }

      if ($isChecked)
        return {
          fg: theme.semantic.color.interactable.error.fg,
          bg: theme.semantic.color.interactable.error['bg-light'],
          border: theme.semantic.color.interactable.error.border,
        };

      return {
        fg: theme.semantic.color.interactable.success.fg,
        bg: theme.semantic.color.interactable.success.bg,
        border: theme.semantic.color.interactable.success.border,
      };
    };

    const activePillColors = getPillColors();

    return css<ActivePillProps>`
      ${commonPillStyles}

      position: absolute;
      width: 50%;
      height: 100%;
      color: ${activePillColors.fg};
      font-weight: ${theme.core.typography.fontWeight.medium};
      background-color: ${activePillColors.bg};
      border: 1px solid ${activePillColors.border};
      transform: ${$isChecked ? `translateX(100%)` : 'translateX(0)'};
      transition: transform 0.1s ease-in-out;
    `;
  },
);

type ElementContainerProps = {
  $elementSize: SwitchElementSize;
  disabled?: boolean;
};

const iconSizeKeyByElementSize: Record<SwitchElementSize, keyof IconSize> = {
  big: 'm',
  small: 's',
};

export const PillsContainer = styled.div<ElementContainerProps>(
  ({ theme, $elementSize, disabled }) => {
    const variant = getSwitchThemeVariant(theme, $elementSize);

    const pillIcon = variant['pill-icon'];
    const pillText = variant['pill-text'];
    const component = variant.switch;

    const iconSize =
      theme.core.iconSize[iconSizeKeyByElementSize[$elementSize]];

    return css`
      position: relative;
      display: flex;
      background-color: ${disabled
        ? component['bg-disabled']
        : component['bg-default']};

      border-radius: ${component.radius};
      cursor: pointer;
      user-select: none;

      svg {
        width: ${iconSize};
        height: ${iconSize};
        fill: ${disabled
          ? pillIcon['icon-disabled']
          : pillIcon['icon-default']};
      }

      &:hover {
        background-color: ${disabled
          ? component['bg-disabled']
          : component['bg-hover']};
      }

      ${InactivePill}:first-of-type {
        padding-right: calc(
          ${pillText['horizontal-padding']} + ${component.stack}
        );
      }

      ${InactivePill}:last-of-type {
        padding-left: calc(
          ${pillText['horizontal-padding']} + ${component.stack}
        );
      }
    `;
  },
);

// Label text

type LabelTextProps = {
  $elementSize: SwitchElementSize;
  $isHidden?: boolean;
};

export const LabelText = styled.div<LabelTextProps>`
  display: ${({ $isHidden }) => $isHidden && 'none'};
  margin-right: ${({ theme }) => theme.core.spacing['8x']};
  color: ${({ theme }) => theme.core.color.neutral[500]};
  font-size: ${({ theme, $elementSize }) =>
    theme.core.typography.fontSize[fontSizeKeyByElementSize[$elementSize]]};
`;

// Input element (hidden)

export const InputElement = styled(InputButtonPrimitive)`
  margin: 0;
  appearance: none;

  &:focus-visible + ${PillsContainer} {
    outline: ${({ theme }) => `4px solid ${theme.core.color.neutral[750]}`};
  }
`;
